import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useContext as useAuthContextHook } from 'react';
import { AuthContext } from './AuthContext';
import { usePremiumContext } from './PremiumContext';
import { StockCategoryFilter } from '../types/stockCategories';
import {
  getUserTier,
  canAccessCategory,
  canVoteSentiment,
  canViewSentimentHistory,
  canJoinPrivateLeaderboard,
  getAccessibleCategories,
  getRestrictedCategories,
  isPremiumCategory,
  isFreeCategory,
  AccessCheckResult,
  UserTier,
  getFeatureFlags
} from '../constants/accessControl';
import { shouldShowPremiumUI, getPremiumConfig } from '../config/premiumConfig';

/**
 * Access Control Context Type
 */
interface AccessControlContextType {
  // User tier
  userTier: UserTier;
  isAnonymous: boolean;
  isFreeUser: boolean;
  isPremiumUser: boolean;

  // Category access
  canAccessCategory: (category: StockCategoryFilter) => AccessCheckResult;
  getAccessibleCategories: () => readonly StockCategoryFilter[];
  getRestrictedCategories: () => readonly StockCategoryFilter[];
  isPremiumCategory: (category: StockCategoryFilter) => boolean;
  isFreeCategory: (category: StockCategoryFilter) => boolean;

  // Feature access
  canVoteSentiment: () => AccessCheckResult;
  canViewSentimentHistory: () => AccessCheckResult;
  canJoinPrivateLeaderboard: (currentCount: number) => AccessCheckResult;

  // Feature flags
  features: {
    canSaveProgress: boolean;
    canViewLeaderboard: boolean;
    canViewOwnRank: boolean;
    canVoteSentiment: boolean;
    canJoinPrivateLeaderboards: boolean;
    canViewSentimentHistory: boolean;
    maxPrivateLeaderboards: number;
  };

  // UI helpers
  shouldShowUpgradePrompt: (accessCheck: AccessCheckResult) => boolean;
  getUpgradeMessage: (accessCheck: AccessCheckResult) => string;
  getCategoryLockStatus: (category: StockCategoryFilter) => {
    isLocked: boolean;
    reason?: string;
    upgradeMessage?: string;
  };

  // Premium UI controls (from premium config)
  shouldShowPremiumButton: boolean;
  shouldShowCategoryLocks: boolean;
  shouldShowUpgradeModals: boolean;
  shouldShowSentimentHistory: boolean;
  premiumMode: 'FREE' | 'HIDE' | 'STANDARD';
}

const AccessControlContext = createContext<AccessControlContextType | undefined>(undefined);

interface AccessControlProviderProps {
  children: ReactNode;
}

/**
 * Access Control Provider
 * Manages all access control logic for the application
 */
export const AccessControlProvider: React.FC<AccessControlProviderProps> = ({ children }) => {
  const authContext = useAuthContextHook(AuthContext);
  const premiumContext = usePremiumContext();

  const user = authContext?.user || null;
  const isAuthenticated = !!user;
  const isPremium = premiumContext.isPremium;

  // Determine user tier
  const userTier = useMemo<UserTier>(() => {
    return getUserTier(isAuthenticated, isPremium);
  }, [isAuthenticated, isPremium]);

  // User tier flags
  const isAnonymous = userTier === 'anonymous';
  const isFreeUser = userTier === 'free';
  const isPremiumUser = userTier === 'premium';

  // Get premium configuration
  const premiumConfig = useMemo(() => getPremiumConfig(), []);

  // Premium UI controls
  const shouldShowPremiumButton = useMemo(() => shouldShowPremiumUI('showButton'), []);
  const shouldShowCategoryLocks = useMemo(() => shouldShowPremiumUI('showLocks'), []);
  const shouldShowUpgradeModals = useMemo(() => shouldShowPremiumUI('showModals'), []);
  const shouldShowSentimentHistory = useMemo(() => shouldShowPremiumUI('showSentimentHistory'), []);

  // Feature access
  const features = useMemo(() => {
    const featureFlags = getFeatureFlags(userTier);
    return {
      ...featureFlags[userTier],
      tier: userTier
    };
  }, [userTier]);

  // Category access check
  const checkCategoryAccess = useMemo(() => {
    return (category: StockCategoryFilter): AccessCheckResult => {
      return canAccessCategory(category, userTier);
    };
  }, [userTier]);

  // Get accessible categories
  const accessibleCategories = useMemo(() => {
    return (): readonly StockCategoryFilter[] => getAccessibleCategories(userTier);
  }, [userTier]);

  // Get restricted categories
  const restrictedCategories = useMemo(() => {
    return (): readonly StockCategoryFilter[] => getRestrictedCategories(userTier);
  }, [userTier]);

  // Sentiment voting check
  const checkSentimentVote = useMemo(() => {
    return (): AccessCheckResult => canVoteSentiment(userTier);
  }, [userTier]);

  // Sentiment history check
  const checkSentimentHistory = useMemo(() => {
    return (): AccessCheckResult => canViewSentimentHistory(userTier);
  }, [userTier]);

  // Private leaderboard check
  const checkPrivateLeaderboard = useMemo(() => {
    return (currentCount: number): AccessCheckResult => {
      return canJoinPrivateLeaderboard(userTier, currentCount);
    };
  }, [userTier]);

  // UI helper: Should show upgrade prompt
  const shouldShowUpgradePrompt = useMemo(() => {
    return (accessCheck: AccessCheckResult): boolean => {
      return !accessCheck.allowed && !!(accessCheck.requiresAuth || accessCheck.requiresPremium);
    };
  }, []);

  // UI helper: Get upgrade message
  const getUpgradeMessage = useMemo(() => {
    return (accessCheck: AccessCheckResult): string => {
      if (!accessCheck.allowed) {
        return accessCheck.upgradeMessage || accessCheck.reason || 'Upgrade to unlock this feature';
      }
      return '';
    };
  }, []);

  // UI helper: Get category lock status
  const getCategoryLockStatus = useMemo(() => {
    return (category: StockCategoryFilter): {
      isLocked: boolean;
      reason?: string;
      upgradeMessage?: string;
    } => {
      const accessCheck = canAccessCategory(category, userTier);
      return {
        isLocked: !accessCheck.allowed,
        reason: accessCheck.reason,
        upgradeMessage: accessCheck.upgradeMessage
      };
    };
  }, [userTier]);

  const value: AccessControlContextType = {
    // User tier
    userTier,
    isAnonymous,
    isFreeUser,
    isPremiumUser,

    // Category access
    canAccessCategory: checkCategoryAccess,
    getAccessibleCategories: accessibleCategories,
    getRestrictedCategories: restrictedCategories,
    isPremiumCategory,
    isFreeCategory,

    // Feature access
    canVoteSentiment: checkSentimentVote,
    canViewSentimentHistory: checkSentimentHistory,
    canJoinPrivateLeaderboard: checkPrivateLeaderboard,

    // Feature flags
    features,

    // UI helpers
    shouldShowUpgradePrompt,
    getUpgradeMessage,
    getCategoryLockStatus,

    // Premium UI controls
    shouldShowPremiumButton,
    shouldShowCategoryLocks,
    shouldShowUpgradeModals,
    shouldShowSentimentHistory,
    premiumMode: premiumConfig.mode
  };

  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
};

/**
 * Hook to use access control context
 */
export const useAccessControl = (): AccessControlContextType => {
  const context = useContext(AccessControlContext);
  if (context === undefined) {
    throw new Error('useAccessControl must be used within AccessControlProvider');
  }
  return context;
};

/**
 * Hook for quick category access check
 */
export const useCategoryAccess = (category: StockCategoryFilter) => {
  const { canAccessCategory, getCategoryLockStatus } = useAccessControl();

  const accessCheck = useMemo(() => canAccessCategory(category), [canAccessCategory, category]);
  const lockStatus = useMemo(() => getCategoryLockStatus(category), [getCategoryLockStatus, category]);

  return {
    canAccess: accessCheck.allowed,
    isLocked: lockStatus.isLocked,
    reason: accessCheck.reason,
    upgradeMessage: accessCheck.upgradeMessage,
    requiresAuth: accessCheck.requiresAuth,
    requiresPremium: accessCheck.requiresPremium,
    accessCheck,
    lockStatus
  };
};

/**
 * Hook for sentiment feature access
 */
export const useSentimentAccess = () => {
  const { canVoteSentiment, canViewSentimentHistory } = useAccessControl();

  const voteAccess = useMemo(() => canVoteSentiment(), [canVoteSentiment]);
  const historyAccess = useMemo(() => canViewSentimentHistory(), [canViewSentimentHistory]);

  return {
    canVote: voteAccess.allowed,
    canViewHistory: historyAccess.allowed,
    voteAccess,
    historyAccess
  };
};

/**
 * Hook for private leaderboard access
 */
export const usePrivateLeaderboardAccess = (currentCount: number = 0) => {
  const { canJoinPrivateLeaderboard, features } = useAccessControl();

  const accessCheck = useMemo(
    () => canJoinPrivateLeaderboard(currentCount),
    [canJoinPrivateLeaderboard, currentCount]
  );

  return {
    canJoin: accessCheck.allowed,
    currentCount,
    maxAllowed: features.maxPrivateLeaderboards,
    remainingSlots: features.maxPrivateLeaderboards === Infinity
      ? Infinity
      : Math.max(0, features.maxPrivateLeaderboards - currentCount),
    accessCheck
  };
};
